// Kundendetails Tab "Terminserien" (08/2026) — Übersicht aller Serien des
// Kunden mit Verwalten (Uhrzeit/Trainer ändern) und Beenden auf einen Klick.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarClock, Repeat, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import SeriesManageDialog from '@/components/appointments/SeriesManageDialog';
import PurgeCancelledButton from '@/components/appointments/PurgeCancelledButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('de-CH', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '–';

export default function CustomerSeriesTab({ customerId }) {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [manageSeriesId, setManageSeriesId] = useState(null);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/customers/${customerId}/appointment-series`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setSeries(res.data?.series || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Terminserien konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4" data-testid="customer-series-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Repeat size={18} className="text-teal-600" />
          <h3 className="font-semibold text-lg">Terminserien</h3>
        </div>
        <PurgeCancelledButton
          customerId={customerId}
          label="Stornierte Termine entfernen"
          variant="outline"
          className="text-rose-600 border-rose-200 hover:bg-rose-50"
          onDone={load}
          data-testid="purge-cancelled-customer-btn"
        />
      </div>

      {loading && <div className="text-sm text-gray-500 py-4">Lade Serien…</div>}

      {!loading && series.length === 0 && (
        <div className="text-sm text-gray-500 py-6 text-center border rounded-lg bg-gray-50" data-testid="customer-series-empty">
          Keine Terminserien für diesen Kunden gefunden.
        </div>
      )}

      {!loading &&
        series.map((s) => (
          <Card key={s.series_id} data-testid={`series-card-${s.series_id}`}>
            <CardContent className="p-4 flex items-start justify-between gap-3 flex-wrap">
              <div className="space-y-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{s.service_name}</span>
                  <Badge variant="secondary">{s.frequency_label}</Badge>
                  {s.upcoming_count === 0 && (
                    <Badge variant="outline" className="text-gray-500">Beendet</Badge>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  {s.weekday ? `${s.weekday}s um ${s.time_hhmm}` : ''}
                  {s.trainer_name ? ` · ${s.trainer_name}` : ''}
                </div>
                <div className="text-sm text-gray-600 flex items-center gap-1.5">
                  <CalendarClock size={14} />
                  Nächster: {fmtDate(s.next_start)} · {s.upcoming_count} zukünftige / {s.total} gesamt
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setManageSeriesId(s.series_id)}
                disabled={s.upcoming_count === 0}
                data-testid={`series-manage-btn-${s.series_id}`}
              >
                <Settings2 size={15} className="mr-1" /> Verwalten
              </Button>
            </CardContent>
          </Card>
        ))}

      <SeriesManageDialog
        open={!!manageSeriesId}
        onOpenChange={(o) => !o && setManageSeriesId(null)}
        seriesId={manageSeriesId}
        onChanged={load}
      />
    </div>
  );
}
